import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailConfirmed = searchParams.get('confirmed') === 'true';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setEmailNotConfirmed(false);
    setResendSuccess(false);

    try {
      const result = await signIn(email, password);

      if (result.error) {
        setError(result.error);
        if (result.error === 'Confirme seu email antes de fazer login') {
          setEmailNotConfirmed(true);
        }
      }
      // Redirect tratado pelo AuthLayout (detecta user e redireciona para /dashboard)
    } catch (err) {
      console.error('Login error:', err);
      setError('Erro inesperado ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email || resending) return;
    setResending(true);
    setResendSuccess(false);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });

    setResending(false);
    if (!error) {
      setResendSuccess(true);
      setError(null);
      setEmailNotConfirmed(false);
    }
  };

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 sm:p-8">
      <h2 className="text-xl font-semibold text-white mb-1">Entrar</h2>
      <p className="text-neutral-400 text-sm mb-6">Acesse sua conta One Form</p>

      {emailConfirmed && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg p-3 mb-4">
          Email confirmado com sucesso! Faça login para continuar.
        </div>
      )}

      {resendSuccess && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg p-3 mb-4">
          Email de confirmação reenviado! Verifique sua caixa de entrada.
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-4">
          {error}
          {emailNotConfirmed && (
            <span>
              {' '}Não recebeu o email?{' '}
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resending}
                className="text-primary-400 hover:text-primary-300 underline transition-colors disabled:opacity-50"
              >
                {resending ? 'Reenviando...' : 'Reenviar'}
              </button>
            </span>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 sm:py-2.5 bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
            placeholder="seu@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-1">
            Senha
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 sm:py-2.5 bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 sm:py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      <div className="mt-4 text-center space-y-2">
        <Link to="/auth/forgot-password" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
          Esqueceu a senha?
        </Link>
        <p className="text-sm text-neutral-400">
          Não tem conta?{' '}
          <Link to="/auth/register" className="text-primary-400 hover:text-primary-300 transition-colors">
            Criar conta
          </Link>
        </p>
      </div>
    </div>
  );
};
